import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { motion } from "motion/react";
import { useFormContext } from "react-hook-form";
import { useParams } from "react-router-dom";
import _ from "lodash";
import PageBreadcrumb from "@/components/PageBreadcrumb";
import Tooltip from "@mui/material/Tooltip";
import useNavigate from "@fuse/hooks/useNavigate";
import { toast } from "react-toastify";
import { CircularProgress } from "@mui/material";
import {
  useCreateAreaMutation,
  useUpdateAreaMutation,
} from "@/store/services/areas";
import { ArrowBack } from "@mui/icons-material";
import Link from "@fuse/core/Link";

function AreaHeader() {
  const routeParams = useParams<{ areaId: string }>();
  const { areaId } = routeParams;

  const [createArea, { isLoading: isAdding }] = useCreateAreaMutation();
  const [updateArea, { isLoading: isSaving }] = useUpdateAreaMutation();
  const methods = useFormContext();

  if (!methods) return null;

  const { formState, watch, getValues, handleSubmit } = methods;
  const { isValid, dirtyFields, isDirty } = formState;

  const navigate = useNavigate();

  const { title } = watch();

  async function handleUpdateArea() {
    const values = getValues();

    const line_items = values.lineItems?.map((item: string) => {
      const prevItem = values?.prevlineItems?.find(
        (prevItem: { name: string }) => prevItem?.name === item
      );
      return prevItem ? { id: prevItem.id, name: item } : { name: item };
    });
    const payload = {
      area_id: areaId,
      name: values.name,
      line_items,
    };
    const response: any = await updateArea(payload);

    if (!response.error) {
      toast.success("Area updated successfully");
      navigate(`/apps/areas`);
    } else {
      toast.error(
        response?.error?.data?.errors?.[0]?.title || "Something went wrong"
      );
    }
  }

  async function handleCreateArea() {
    const values = getValues();
    const payload = {
      name: values.name,
      line_items: values.lineItems?.map((item: string) => ({ name: item })),
    };
    const response: any = await createArea(payload);

    if (!response.error) {
      toast.success("Area created successfully");
      navigate(`/apps/areas`);
    } else {
      toast.error(
        response?.error?.data?.errors?.[0]?.title || "Something went wrong"
      );
    }
  }

  return (
    <div className="flex flex-col sm:flex-row flex-1 w-full items-end justify-between space-y-2 sm:space-y-0 py-6 sm:py-8">
      <div className="flex flex-col items-start space-y-2 sm:space-y-0 w-full sm:max-w-full min-w-0">
        <motion.div
          initial={{
            x: 20,
            opacity: 0,
          }}
          animate={{
            x: 0,
            opacity: 1,
            transition: { delay: 0.3 },
          }}
        >
          <PageBreadcrumb className="mb-2" />
        </motion.div>

        <div className="flex items-center max-w-full space-x-3">
          <motion.div
            className="flex items-start gap-2 min-w-0"
            initial={{ x: -20 }}
            animate={{ x: 0, transition: { delay: 0.3 } }}
          >
            <div className="w-full flex items-center space-x-2">
              <Link href="/apps/areas">
                <ArrowBack />
              </Link>
            </div>
            <div>
              <Typography className="text-lg sm:text-2xl truncate font-semibold">
                {title || `${areaId === "new" ? "New" : "Edit"} Area`}
              </Typography>
              <Typography variant="caption" className="font-medium">
                Area Detail
              </Typography>
            </div>
          </motion.div>
        </div>
      </div>
      <motion.div
        className="flex justify-end w-full"
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0, transition: { delay: 0.3 } }}
      >
        {areaId !== "new" ? (
          <Tooltip
            title={
              !isDirty && isValid
                ? "No changes detected."
                : ""
            }
            disableHoverListener={isDirty || !isValid}
            arrow
          >
            <span>
              <Button
                className="whitespace-nowrap mx-1"
                variant="contained"
                color="secondary"
                disabled={!isValid || isSaving || !isDirty}
                onClick={handleSubmit(handleUpdateArea)}
                startIcon={isSaving && <CircularProgress size={20} />}
              >
                Save
              </Button>
            </span>
          </Tooltip>
        ) : (
          <Button
            className="whitespace-nowrap mx-1"
            variant="contained"
            color="secondary"
            disabled={_.isEmpty(dirtyFields) || isAdding}
            onClick={handleSubmit(handleCreateArea)}
            startIcon={isAdding && <CircularProgress size={20} />}
          >
            Add
          </Button>
        )}
      </motion.div>
    </div>
  );
}

export default AreaHeader;
